import React, {useEffect, useState} from 'react';
import {observer} from 'mobx-react-lite';
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import TableSortLabel from '@mui/material/TableSortLabel';
import {PurchaseHistoryDialogProps} from '../../../../entity/props/marketplace/purchase-history-dialog-props';
import {useService} from '../../../../infrastructure/service/service';
import {MarketplacePurchaseRecordResponse} from '../../../../entity/resource/marketplace-purchase-record';
import {State} from '../../../../entity/model/state';
import {County} from '../../../../entity/model/county';
import LoadingComponent from '../widget/LoadingComponent';
import TablePagination from '../widget/TablePagination';
import moment from 'moment';

import '../../../../assets/css/style.css';

const PurchaseHistoryDialog = (props: PurchaseHistoryDialogProps): JSX.Element => {
  const {marketplaceService, userService, stateService, countyService} = useService();

  const [loading, setLoading] = useState<boolean>(true);
  const [purchaseRecords, setPurchaseRecords] = useState<MarketplacePurchaseRecordResponse[]>([]);
  const [states, setStates] = useState<State[]>([]);
  const [counties, setCounties] = useState<County[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(0);

  const pageSize = 100;
  const tableHeaders = ['Document Name', 'State', 'County', 'Book Type', 'Pages', 'Purchase Date', 'Action'];

  useEffect(() => {
    const init = async (): Promise<void> => {
      setLoading(true);

      const [user, allStates, allCounties] = await Promise.all([
        userService.getCurrentUser(),
        stateService.getStates(),
        countyService.getCounties()
      ]);
      setStates(allStates);
      setCounties(allCounties);

      if (user?.organizationID) {
        const records = await marketplaceService.getMarketplacePurchaseRecords(user.organizationID);
        if (records) {
          setPurchaseRecords(records);

          const totalPages = Math.ceil(records.length / pageSize);
          setTotalPages(totalPages);
          setCurrentPage(1);
        }
      }

      setLoading(false);
    }

    init();
  }, []);

  const handleClose = (): void => {
    props.setOpen(false);
    setPurchaseRecords([]);
    setCurrentPage(1);
    setTotalPages(0);
  }

  const handlePaginationChange = (event: React.ChangeEvent<unknown>, activePage: number): void => {
    setCurrentPage(activePage);
  }

  const getCurrentPageData = (): MarketplacePurchaseRecordResponse[] => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return purchaseRecords.slice(startIndex, endIndex);
  }

  const handlePurchase = (record: MarketplacePurchaseRecordResponse): void => {
    props.setOpen(false);
        
    if (props.onAutoFill && props.setFieldValue && props.setFieldTouched)
      props.onAutoFill(record, props.setFieldValue, props.setFieldTouched);

  }

  const getStateName = (stateID: string): string => {
    const state = states.find(s => s.id === stateID);
    return state?.shortName || '';
  }

  const getCountyName = (countyID: string): string => {
    const county = counties.find(c => c.id === countyID);
    return county?.name || '';
  }

  const renderTableRow = (record: MarketplacePurchaseRecordResponse): JSX.Element => {
    const { marketplacePurchaseRecord, marketplaceDocumentMetadata } = record;

    return (
      <TableRow key={marketplacePurchaseRecord.id} className={'table-cell'} hover>
        <TableCell className={'no-wrap table-cell-text'} align={'left'}>
          {marketplaceDocumentMetadata.documentKey}
        </TableCell>
        <TableCell className={'no-wrap table-cell-text'} align={'left'}>
          {getStateName(marketplaceDocumentMetadata.stateID)}
        </TableCell>
        <TableCell className={'no-wrap table-cell-text'} align={'left'}>
          {getCountyName(marketplaceDocumentMetadata.countyID)}
        </TableCell>
        <TableCell className={'no-wrap table-cell-text'} align={'left'}>
          {marketplaceDocumentMetadata.bookType}
        </TableCell>
        <TableCell className={'no-wrap table-cell-text'} align={'left'}>
          {`${marketplacePurchaseRecord.fromPage} - ${marketplacePurchaseRecord.toPage}`}
        </TableCell>
        <TableCell className={'no-wrap table-cell-text'} align={'left'}>
          {moment.utc(marketplacePurchaseRecord.createdAt).format('YYYY-MM-DD')}
        </TableCell>
        <TableCell className={'no-wrap table-cell-text'} align={'left'}>
          <IconButton
            onClick={() => handlePurchase(record)}
            title="Re-download"
            className={'download-icon-button'}>
            <DownloadIcon />
          </IconButton>
        </TableCell>
      </TableRow>
    );
  }

  const renderEmptyRow = (): JSX.Element => {
    return (
      <TableRow>
        <TableCell colSpan={7} align='center' className='marketplace-empty-cell'>
          <Typography className='empty-message'>
            No purchase history found.
          </Typography>
        </TableCell>
      </TableRow>
    );
  }

  return (
    <Dialog
      open={props.open}
      onClose={handleClose}
      fullScreen
      className={'zitles-dialog fullscreen'}>
      <DialogTitle className={'zitles-dialog-title'}>
        <span>Purchase History</span>
        <IconButton id={'btn-purchase-history-dialog-close'} onClick={handleClose}><CloseIcon className={'dialog-icon'}/></IconButton>
      </DialogTitle>
      <DialogContent dividers className={'zitles-dialog-content large'}>
        {
          loading
            ? <LoadingComponent />
            : <Box>
                <Paper className={'table-container'} elevation={0}>
                  <TableContainer className={'zitles-table purchase-history-table'} component={Paper}>
                    <Table>
                      <TableHead>
                        <TableRow className={'table-header'}>
                          {
                            tableHeaders.map((header: string) => (
                              <TableCell key={header} className={'no-wrap'} align='left' padding='normal'>
                                <TableSortLabel className={'table-header-text'} active={true} direction='desc'>{header}
                                </TableSortLabel>
                              </TableCell>))
                          }
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {
                          purchaseRecords.length > 0
                            ? getCurrentPageData().map(renderTableRow)
                            : renderEmptyRow()
                        }
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
                {
                  purchaseRecords.length > 100 && totalPages > 1 &&
                    <TablePagination
                      currentPage={currentPage}
                      totalPages={totalPages}
                      onChange={handlePaginationChange}/>
                }
              </Box>
        }
      </DialogContent>
    </Dialog>
  );
}

export default observer(PurchaseHistoryDialog);
